import api from "../../../api";

const BASE = "/api/communications";

// ---- Campaigns ----
export const fetchCampaigns = (params = {}) =>
  api.get(`${BASE}/campaigns/`, { params }).then((r) => r.data);

export const fetchCampaign = (id) =>
  api.get(`${BASE}/campaigns/${id}/`).then((r) => r.data);

export const createCampaign = (payload) =>
  api.post(`${BASE}/campaigns/`, payload).then((r) => r.data);

export const updateCampaign = (id, payload) =>
  api.patch(`${BASE}/campaigns/${id}/`, payload).then((r) => r.data);

export const deleteCampaign = (id) =>
  api.delete(`${BASE}/campaigns/${id}/`).then((r) => r.data);

export const duplicateCampaign = (id) =>
  api.post(`${BASE}/campaigns/${id}/duplicate/`).then((r) => r.data);

export const fetchCampaignAudience = (id) =>
  api.get(`${BASE}/campaigns/${id}/audience/`).then((r) => r.data);

export const testSendCampaign = (id, to) =>
  api.post(`${BASE}/campaigns/${id}/test-send/`, { to }).then((r) => r.data);

export const sendCampaign = (id, scheduled_at = null) =>
  api
    .post(`${BASE}/campaigns/${id}/send/`, scheduled_at ? { scheduled_at } : {})
    .then((r) => r.data);

export const fetchCampaignRecipients = (id, params = {}) =>
  api.get(`${BASE}/campaigns/${id}/recipients/`, { params }).then((r) => r.data);

// ---- Ad-hoc blasts (a Find Buyers send) ----
// A blast is not a Campaign - Find Buyers sends straight through the engine and
// used to leave no send-level record at all, which is why an agent's blast never
// appeared on the Campaigns tab (Monday 13057482228). It now does, as a row with
// `kind: "blast"`. These are read-only: a blast has already gone out, so there
// is nothing to edit, schedule or duplicate.
export const fetchSendBatch = (id) =>
  api.get(`${BASE}/sends/${id}/`).then((r) => r.data);

// The people one blast went to, mapped onto the SAME shape the campaign
// drill-down uses, so one recipients panel can render either. A blast's rows are
// CommunicationLog rows: they carry `person_email` / `occurred_at` where a
// campaign recipient carries `to_email` / `sent_at`.
export const fetchSendBatchRecipients = (id, params = {}) =>
  api.get(`${BASE}/sends/${id}/recipients/`, { params }).then((r) =>
    (Array.isArray(r.data) ? r.data : []).map((row) => ({
      id: row.id,
      contact: row.contact,
      contact_name: row.contact_name || "",
      contact_phone: row.person_phone || "",
      to_email: row.person_email || "",
      to_phone: row.person_phone || "",
      status: row.status,
      status_display: row.status,
      sent_at: row.occurred_at,
      delivered_at: row.delivered_at,
      opened_at: row.opened_at,
      clicked_at: row.clicked_at,
      unsubscribed_at: row.unsubscribed_at,
      error: "",
    }))
  );

export const aiWriteCampaign = (payload) =>
  api.post(`${BASE}/campaigns/ai-write/`, payload).then((r) => r.data);

// One image or PDF dropped on the "Write with GeeVee" box. Images come back
// with a hosted URL (placed in the generated email); PDFs come back with the
// text we read out of them (GeeVee writes the copy from it). The shared axios
// instance defaults to "application/json", so this clears that default
// rather than replacing it with a hardcoded "multipart/form-data" string: a
// manually-set Content-Type has no boundary param, and once it's set
// explicitly the browser won't add one itself, which breaks Django's
// multipart parser. Setting it to `undefined` removes the header entirely
// so the browser computes the correct boundary on its own.
export const uploadAiAttachment = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`${BASE}/campaigns/ai-attachment/`, form, {
      headers: { "Content-Type": undefined },
    })
    .then((r) => r.data);
};

// Real email attachments on a campaign (CRM-2026-00032). The file is stored now
// and attached to every email the campaign sends. Content-Type is cleared so the
// browser sets the multipart boundary itself (a manual value has none, which
// breaks Django's multipart parser).
export const uploadCampaignAttachment = (campaignId, file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`${BASE}/campaigns/${campaignId}/attachments/`, form, {
      headers: { "Content-Type": undefined },
    })
    .then((r) => r.data);
};

export const deleteCampaignAttachment = (campaignId, attachmentId) =>
  api.delete(`${BASE}/campaigns/${campaignId}/attachments/${attachmentId}/`).then((r) => r.data);

// ---- Segments (audiences / lists) ----
export const fetchSegments = () =>
  api.get(`${BASE}/segments/`).then((r) => r.data);

export const fetchSegment = (id) =>
  api.get(`${BASE}/segments/${id}/`).then((r) => r.data);

// The resolved people in a segment (who's actually in the list), with
// property/date context for the auto-groups. -> { total, count, rows }
export const fetchSegmentMembers = (id) =>
  api.get(`${BASE}/segments/${id}/members/`).then((r) => r.data);

export const createSegment = (payload) =>
  api.post(`${BASE}/segments/`, payload).then((r) => r.data);

export const updateSegment = (id, payload) =>
  api.patch(`${BASE}/segments/${id}/`, payload).then((r) => r.data);

export const deleteSegment = (id) =>
  api.delete(`${BASE}/segments/${id}/`).then((r) => r.data);

export const previewSegment = ({
  filter_json = {},
  channel = null,
  segment_ids = [],
  pinned_contact_ids = [],
} = {}) =>
  api
    .post(`${BASE}/segments/preview/`, {
      filter_json,
      channel,
      segment_ids,
      pinned_contact_ids,
    })
    .then((r) => r.data);

// The "Buyers Match" table for a listing: every buyer whose saved preferences
// match it, with the match score / reasons and whether this channel can reach
// them. -> { count, sendable_count, blocked_count, rows: [...] }
//
// Pass `criteria` (with at least one ticked suburb) to run the agent's own
// search, the same engine the "Find Buyers for a Property" panel uses, instead
// of the automatic match off the listing.
export const fetchBuyerMatchTable = (property_id, channel = "email", criteria = null) =>
  criteria
    ? api
        .post(`${BASE}/segments/buyer-match/`, { ...criteria, property_id, channel })
        .then((r) => r.data)
    : api
        .get(`${BASE}/segments/buyer-match/`, { params: { property_id, channel } })
        .then((r) => r.data);

// Starting criteria for the Buyers Match search plus the "suburbs buyers have
// marked" tick-list. -> { defaults, suburbs, radius_km, origin_resolved }
export const fetchBuyerMatchCriteria = ({ property_id, suburb, state, lat, lng, radius_km } = {}) =>
  api
    .get(`${BASE}/segments/buyer-match/criteria/`, {
      params: { property_id, suburb, state, lat, lng, radius_km },
    })
    .then((r) => r.data);

// Find a buyer by name / email / phone so they can be ADDED to the send, even
// when their saved preferences don't match the listing. -> { count, rows: [...] }
export const searchBuyersToAdd = (q, channel = "email") =>
  api
    .get(`${BASE}/segments/buyer-match/search/`, { params: { q, channel } })
    .then((r) => r.data);

// Re-read specific buyers for a channel. Used when an email campaign is switched
// to SMS: someone added by hand must not stay "sendable" on a channel that can't
// reach them. -> { count, rows: [...] }
export const refreshBuyersForChannel = (ids, channel = "email") =>
  api
    .get(`${BASE}/segments/buyer-match/search/`, {
      params: { ids: (ids || []).join(","), channel },
    })
    .then((r) => r.data);

export const fetchFilterOptions = () =>
  api.get(`${BASE}/segments/filter-options/`).then((r) => r.data);

// Lightweight contact lookup for the "Manually add people" search box.
export const searchContactsForList = (q) =>
  api
    .get(`${BASE}/segments/contact-search/`, { params: { q } })
    .then((r) => r.data);

// Add one person to a campaign by their details: reuses the contact with that
// email / mobile, or adds them to Contacts. -> {id, name, email, phone, created}
export const quickAddContact = ({ name = "", email = "", phone = "" } = {}) =>
  api
    .post(`${BASE}/segments/quick-add-contact/`, { name, email, phone })
    .then((r) => r.data);

// Parse a pasted blob of emails into individual, deduped addresses and report
// which are already contacts vs new. Preview only - creates nothing.
export const parsePastedEmails = (text) =>
  api.post(`${BASE}/segments/paste-parse/`, { text }).then((r) => r.data);

// ---- Templates ----
export const fetchTemplates = (params = {}) =>
  api.get(`${BASE}/templates/`, { params }).then((r) => r.data);

export const createTemplate = (payload) =>
  api.post(`${BASE}/templates/`, payload).then((r) => r.data);

export const updateTemplate = (id, payload) =>
  api.patch(`${BASE}/templates/${id}/`, payload).then((r) => r.data);

export const deleteTemplate = (id) =>
  api.delete(`${BASE}/templates/${id}/`).then((r) => r.data);

// The audience tabs for the template picker, with a live count each. Served from
// the backend's MessageTemplate.AUDIENCE_CHOICES so the tabs and the stored
// values can't drift apart.
export const fetchTemplateAudiences = (channel = "sms") =>
  api.get(`${BASE}/templates/audiences/`, { params: { channel } }).then((r) => r.data);

// ---- Merge tags ----
// The one vocabulary of {{tags}}. Read instead of hard-coding a list in the FE:
// the composers used to advertise four tags while templates could reference any
// tag at all, and an unknown tag sends as an empty string.
export const fetchMergeTags = () =>
  api.get(`${BASE}/merge-tags/`).then((r) => r.data);

// Pre-send guard: "can this draft's compose-scope tags actually be filled in?"
// Returns {resolved, unresolved: [{name, label, reason}], text, subject}.
export const checkMergeTags = (payload) =>
  api.post(`${BASE}/merge-tags/check/`, payload).then((r) => r.data);

// ---- Property picker (populate email from a real listing) ----
export const fetchCampaignProperties = (q = "") =>
  api
    .get(`${BASE}/properties/`, { params: q ? { q } : {} })
    .then((r) => r.data);

export const fetchPropertyEmailData = (id) =>
  api.get(`${BASE}/properties/${id}/email-data/`).then((r) => r.data);

// Every bulk email/text sent from one listing's Enquiries / Attendees / Buyers
// tabs, with how many opened and clicked (Paul Jimoulis, CRM-2026-00072).
// Returns { property_id, count, totals, sends: [...] }.
export const fetchPropertySends = (id) =>
  api.get(`${BASE}/properties/${id}/sends/`).then((r) => r.data);

// One blast: who it went to, and who opened / clicked / unsubscribed.
export const fetchSendRecipients = (id, params = {}) =>
  api.get(`${BASE}/sends/${id}/recipients/`, { params }).then((r) => r.data);

// ---- SMS credit balance (Mobile Message account) ----
// Returns { credits: <number>|null, provider }. credits is null when the
// balance can't be read or the provider isn't Mobile Message.
export const fetchSmsCreditBalance = () =>
  api.get(`${BASE}/sms/credit-balance/`).then((r) => r.data);

// ---- Agents' own mobiles as SMS senders (super admin only) ----
// Monday 13377714139. GET -> { senders, agents, error }; POST texts the agent a
// Mobile Message verification link and returns the same shape plus `detail`.
export const fetchSmsSenders = () =>
  api.get(`${BASE}/sms/senders/`).then((r) => r.data);

export const registerSmsSender = (agent_id, number) =>
  api.post(`${BASE}/sms/senders/`, { agent_id, number }).then((r) => r.data);

// What a text sent as this agent (blank = me) goes out from, any staff.
// -> { own_number, sender, intro }. `intro` is the opening line the law asks
// for on a text from an agent's own mobile (Monday 13392775822).
export const fetchSmsSenderFor = (agentId) =>
  api.get(`${BASE}/sms/sender/`, { params: agentId ? { agent_id: agentId } : {} })
    .then((r) => r.data);

// ---- The Sent log ----
// How many emails/texts have gone out, who received them, and the SMS credit
// balance (Monday 12887883570). Reads CommunicationLog, so a Find Buyers blast
// is counted as well as a campaign.
// params: { days | start, end, channel, source, status, scope, q, page, page_size }
export const fetchSentActivity = (params = {}) =>
  api.get(`${BASE}/sent/`, { params }).then((r) => r.data);

// The Sent log rolled up ONE ROW PER SEND rather than one per message, with the
// message that was sent on each row (Christian Gravias, Monday 13057482228:
// "Please break it down as per campaign rather then individual texts... I want
// to also be able to read the message that the agents sent at the campaign
// level"). Each group carries a `key`; passing it back as `group_key` returns
// that send's individual messages.
export const fetchSentGroups = (params = {}) =>
  api.get(`${BASE}/sent/`, { params: { ...params, group: "send" } }).then((r) => r.data);

// The email a send ACTUALLY delivered - brand wrap, images, links and all -
// rebuilt server-side from the composed body (Christian Gravias, Monday
// 13057482228: "Please show a link to click to view the actual email that went
// out, not just text. Would like to see photos, links, layout etc.").
export const fetchSentMessage = (group_key, params = {}) =>
  api.get(`${BASE}/sent/message/`, { params: { ...params, group_key } })
    .then((r) => r.data);

// Everyone who has opted out of email, texts or the buyer portal, newest first
// (Paul Jimoulis, CRM-2026-00093: "I cant see who has unsubscribed").
// params: { channel: email|sms|portal, how: optouts|self|staff|bounce|all,
//           q, days, page, page_size, agency_wide, agents }
// Returns { count, page, page_size, agency_wide, summary, results: [...] }.
export const fetchUnsubscribed = (params = {}) =>
  api.get(`${BASE}/unsubscribed/`, { params }).then((r) => r.data);

export const fetchSentSources = () =>
  api.get(`${BASE}/sent/sources/`).then((r) => r.data);

// ---- Analytics + timeline ----
export const fetchCommsAnalytics = () =>
  api.get(`${BASE}/analytics/summary/`).then((r) => r.data);

export const fetchCommunicationTimeline = (params) =>
  api.get(`${BASE}/timeline/`, { params }).then((r) => r.data);

// ---- Who clicked the two tail links every marketing message carries ----
// "Join the Collings platform" and "Update your property preferences" are
// rewritten per recipient on the way out, so these answer WHICH buyer tapped
// which one (CG, Monday 13068863308). params: { kind, clicked, search, days }.
export const fetchLinkClicks = (params = {}) =>
  api.get(`${BASE}/analytics/link-clicks/`, { params }).then((r) => r.data);

// One-press buyer-portal invite for somebody who tapped "join the Off Market
// Portal" and never signed up. Keyed on the click row, not a contact: some of
// these people reached us through a pasted list and have no CRM contact behind
// them, and they are exactly who the button is for.
export const inviteLinkClicker = (id) =>
  api.post(`${BASE}/analytics/link-clicks/${id}/invite/`).then((r) => r.data);

// ---- Lead attribution ----
// Joins the UTMs stamped on every campaign link back to the portal visits,
// signups and enquiries that followed. params: { start, end } as YYYY-MM-DD.
export const fetchCampaignAttribution = (params = {}) =>
  api.get(`${BASE}/analytics/attribution/`, { params }).then((r) => r.data);

// The named people behind one campaign's lead and signup counts.
export const fetchCampaignAttributionDetail = (id) =>
  api.get(`${BASE}/analytics/attribution/${id}/`).then((r) => r.data);
